import { useMutation } from '@tanstack/react-query';
import { CornerUpRight, Link2, Loader2, User, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { FormSubmissionActivity } from './types';
import type { Id } from '@minty/shared/ids';
import { useCan } from '~/hooks/use-can';
import { useConfirm } from '~/components/confirm-dialog';
import { Button } from '~/components/ui/button';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

/**
 * Answering what an activity claims about the contact whose feed this is.
 *
 * This is the whole conflict-resolution UI, and it needs no contact picker
 * because both sides are already known here. The server sends only claims still
 * worth answering, so there is nothing to filter out.
 */

type Claim = FormSubmissionActivity['claims'][number];
type ChannelClaim = Extract<Claim, { kind: 'phone' | 'email' }>;
type NameClaim = Extract<Claim, { kind: 'name' }>;

function ActivityClaimsContent({
  activityId,
  submissionId,
  claims,
  contactId,
  contactName,
}: {
  activityId: Id<'activities'>;
  /** Only an enquiry can be filed under somebody else; a call follows its
   *  channel. Absent on every other kind of activity. */
  submissionId?: Id<'formSubmissions'>;
  claims: Array<Claim>;
  contactId: Id<'contacts'>;
  contactName: string;
}) {
  const channels = claims.filter(
    (claim): claim is ChannelClaim => claim.kind !== 'name',
  );
  const names = claims.filter(
    (claim): claim is NameClaim => claim.kind === 'name',
  );

  return (
    <>
      {channels.map((claim) => (
        <ClaimRow
          key={claim.value}
          activityId={activityId}
          claim={claim}
          contactId={contactId}
        />
      ))}
      {names.map((claim) => (
        <NameRow
          key={claim.value}
          activityId={activityId}
          claim={claim}
          contactId={contactId}
        />
      ))}
      {submissionId && (
        <FileUnderRow
          submissionId={submissionId}
          claims={channels}
          contactName={contactName}
        />
      )}
    </>
  );
}

/** Stop offering this one. Scoped to this activity and never cleared. */
function DismissButton({
  activityId,
  kind,
  value,
}: {
  activityId: Id<'activities'>;
  kind: Claim['kind'];
  value: string;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: dismissClaim } = useMutation(
    trpc.activities.claims.dismissClaim.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);

  return (
    <Button
      type="button"
      variant="ghost"
      size="xs"
      disabled={isSaving}
      title={t('Not theirs')}
      onClick={() => {
        setIsSaving(true);
        void dismissClaim({ activityId, kind, value })
          .catch(() => notify.error(t('Unable to dismiss this.')))
          .finally(() => setIsSaving(false));
      }}
      className="shrink-0 text-muted-foreground"
    >
      <X className="size-3.5" strokeWidth={2.5} />
    </Button>
  );
}

function ClaimRow({
  activityId,
  claim,
  contactId,
}: {
  activityId: Id<'activities'>;
  claim: ChannelClaim;
  contactId: Id<'contacts'>;
}) {
  const { t } = useTranslation('tasks');
  const confirm = useConfirm();
  const trpc = useTRPC();
  const { mutateAsync: addChannel } = useMutation(
    trpc.contacts.ownership.addChannel.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const isElsewhere = claim.ownerContactId !== null;
  const handleAttach = async () => {
    if (
      isElsewhere &&
      !(await confirm({
        title: t(
          'Move {{value}} here? Its calls, texts and email move with it, away from {{owner}}.',
          { value: claim.value, owner: claim.ownerName ?? '' },
        ),
        confirmLabel: t('Move', { ns: 'translation' }),
        isDestructive: true,
      }))
    ) {
      return;
    }

    setIsSaving(true);

    try {
      await addChannel({
        contactId,
        value: claim.value,
        takeFromOwner: isElsewhere,
      });
      notify.success(t('{{value}} now belongs here.', { value: claim.value }));
    } catch {
      notify.error(t('Unable to move {{value}}.', { value: claim.value }));
    } finally {
      setIsSaving(false);
    }
  };

  let icon = <Link2 className="size-3.5" strokeWidth={2.5} />;

  if (isSaving) {
    icon = <Loader2 className="size-3.5 animate-spin" strokeWidth={2.5} />;
  }

  return (
    <ClaimShell
      label={
        isElsewhere
          ? t('{{value}} belongs to {{owner}}', {
              value: claim.value,
              owner: claim.ownerName ?? '',
            })
          : t('{{value}} belongs to nobody yet', { value: claim.value })
      }
      activityId={activityId}
      kind={claim.kind}
      value={claim.value}
    >
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled={isSaving}
        onClick={() => void handleAttach()}
        className="shrink-0"
      >
        {icon}
        {isElsewhere ? t('Move it here') : t('Attach')}
      </Button>
    </ClaimShell>
  );
}

/** A name the activity gave that the contact does not go by. */
function NameRow({
  activityId,
  claim,
  contactId,
}: {
  activityId: Id<'activities'>;
  claim: NameClaim;
  contactId: Id<'contacts'>;
}) {
  const { t } = useTranslation('tasks');
  const trpc = useTRPC();
  const { mutateAsync: setName } = useMutation(
    trpc.contacts.directory.setName.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);

  let icon = <User className="size-3.5" strokeWidth={2.5} />;

  if (isSaving) {
    icon = <Loader2 className="size-3.5 animate-spin" strokeWidth={2.5} />;
  }

  return (
    <ClaimShell
      label={
        claim.current === null
          ? t('This person has no name yet — {{value}}?', {
              value: claim.value,
            })
          : t('Called {{current}} here, {{value}} on this enquiry', {
              current: claim.current,
              value: claim.value,
            })
      }
      activityId={activityId}
      kind="name"
      value={claim.value}
    >
      <Button
        type="button"
        variant="outline"
        size="xs"
        disabled={isSaving}
        onClick={() => {
          setIsSaving(true);
          void setName({ contactId, name: claim.value })
            .catch(() => notify.error(t('Unable to rename.')))
            .finally(() => setIsSaving(false));
        }}
        className="shrink-0"
      >
        {icon}
        {claim.current === null ? t('Set name') : t('Rename')}
      </Button>
    </ClaimShell>
  );
}

function ClaimShell({
  label,
  activityId,
  kind,
  value,
  children,
}: {
  label: string;
  activityId: Id<'activities'>;
  kind: Claim['kind'];
  value: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-warning/40 bg-warning/5 px-3 py-1.5">
      <p className="min-w-0 truncate text-xs text-muted-foreground">{label}</p>
      <div className="flex shrink-0 items-center gap-1">
        {children}
        <DismissButton activityId={activityId} kind={kind} value={value} />
      </div>
    </div>
  );
}

/**
 * Move the enquiry itself onto one of the people who own what it typed.
 *
 * A different act from attaching a channel, and the sharp edge worth being
 * legible about: a submission lives on exactly one contact, so filing it under
 * one owner means the other's feed will not show it at all.
 */
function FileUnderRow({
  submissionId,
  claims,
  contactName,
}: {
  submissionId: Id<'formSubmissions'>;
  claims: Array<ChannelClaim>;
  contactName: string;
}) {
  const { t } = useTranslation('tasks');
  const confirm = useConfirm();
  const trpc = useTRPC();
  const { mutateAsync: restamp } = useMutation(
    trpc.forms.submissions.restampSubmission.mutationOptions(),
  );
  const [isSaving, setIsSaving] = useState(false);
  const owners = claims.filter((claim) => claim.ownerContactId !== null);

  if (owners.length === 0) {
    return null;
  }

  const handleFileUnder = async (
    claim: ChannelClaim,
    toContactId: Id<'contacts'>,
  ) => {
    if (
      !(await confirm({
        title: t('File this enquiry under {{owner}}? It leaves {{from}}.', {
          owner: claim.ownerName ?? '',
          from: contactName,
        }),
        confirmLabel: t('Move', { ns: 'translation' }),
        isDestructive: true,
      }))
    ) {
      return;
    }

    setIsSaving(true);

    try {
      await restamp({ submissionId, toContactId });
      notify.success(t('Enquiry moved.'));
    } catch {
      notify.error(t('Unable to move the enquiry.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1">
      <span className="text-xs text-muted-foreground">
        {t('File this enquiry under:')}
      </span>
      {owners.map((claim) => (
        <Button
          key={claim.value}
          type="button"
          variant="ghost"
          size="xs"
          disabled={isSaving}
          onClick={() => {
            if (claim.ownerContactId) {
              void handleFileUnder(claim, claim.ownerContactId);
            }
          }}
          className="text-xs text-info hover:text-info"
        >
          <CornerUpRight className="size-3.5" strokeWidth={2.5} />
          {claim.ownerName ?? claim.value}
        </Button>
      ))}
    </div>
  );
}

export function ActivityClaims(
  props: Parameters<typeof ActivityClaimsContent>[0],
) {
  const allowed = useCan('reception');
  return allowed ? <ActivityClaimsContent {...props} /> : null;
}
